import { useCallback, useEffect, useMemo, useState } from "react";
import {
  filterMentionUsers,
  getActiveMention,
  insertMention,
  mergeMentionUsers,
  MENTION_DROPDOWN_LIMIT,
} from "./chatMentionUtils";

const useChatMentions = ({
  value = "",
  onChange,
  users = [],
  textareaRef,
  searchUsers,
  currentUserId,
} = {}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [mention, setMention] = useState(null);
  const [remoteUsers, setRemoteUsers] = useState([]);

  const filteredUsers = useMemo(() => {
    if (!mention) return [];
    return filterMentionUsers(
      mergeMentionUsers(users, remoteUsers).filter(
        (user) => !currentUserId || String(user._id) !== String(currentUserId)
      ),
      mention.query
    ).slice(0, MENTION_DROPDOWN_LIMIT);
  }, [currentUserId, mention, remoteUsers, users]);

  const open = Boolean(mention) && filteredUsers.length > 0;

  const syncMention = useCallback((text, caret) => {
    const next = getActiveMention(text, caret);
    setMention(next);
    setActiveIndex(0);
    if (!next) setRemoteUsers([]);
  }, []);

  useEffect(() => {
    if (!mention || !searchUsers) return undefined;
    const query = mention.query;
    if (!query) {
      setRemoteUsers([]);
      return undefined;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const found = await searchUsers(query);
        if (!cancelled) setRemoteUsers(Array.isArray(found) ? found : []);
      } catch {
        if (!cancelled) setRemoteUsers([]);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [mention, searchUsers]);

  const handleChange = useCallback(
    (event) => {
      const text = event.target.value;
      const caret = event.target.selectionStart ?? text.length;
      onChange?.(text, event);
      syncMention(text, caret);
    },
    [onChange, syncMention]
  );

  const selectUser = useCallback(
    (user) => {
      if (!user?.userName) return;
      const caret =
        textareaRef?.current?.selectionStart ?? value.length;
      const current = mention || getActiveMention(value, caret);
      if (!current) return;

      const next = insertMention(value, current, user.userName);
      onChange?.(next.text);
      setMention(null);
      setRemoteUsers([]);
      setActiveIndex(0);

      window.requestAnimationFrame(() => {
        const el = textareaRef?.current;
        if (!el) return;
        el.focus();
        el.setSelectionRange(next.caret, next.caret);
      });
    },
    [mention, onChange, textareaRef, value]
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (!open) return false;

      if (event.key === "ArrowDown") {
        event.preventDefault();
        setActiveIndex((prev) => (prev + 1) % filteredUsers.length);
        return true;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setActiveIndex(
          (prev) => (prev - 1 + filteredUsers.length) % filteredUsers.length
        );
        return true;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        selectUser(filteredUsers[activeIndex] || filteredUsers[0]);
        return true;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        setMention(null);
        setRemoteUsers([]);
        return true;
      }
      return false;
    },
    [activeIndex, filteredUsers, open, selectUser]
  );

  return {
    open,
    filteredUsers,
    activeIndex,
    handleChange,
    handleKeyDown,
    selectUser,
  };
};

export default useChatMentions;
